import get from "lodash.get";
import i18n from "src/translations";

import remoteImageToDataURL from "../../../utils/remote_image_to_data_url";
import { STANDARD_FRAME_TYPES } from "../../../selectors/data_selectors/frames/constants";

function decorateArc(ctx, x, y, text) {
  ctx.stroke();
  ctx.globalAlpha = 0.6;
  ctx.fillStyle = "#000000";
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.fillStyle = "#fff";
  ctx.fillText(text, x, y);
}

function getAnnotatedAndTracedThermalImage(
  thermalFrame,
  showAnnotations = true,
  resolve,
  reject
) {
  try {
    const canvas = document.createElement("canvas");

    if (!thermalFrame.mediumUrl) {
      resolve(canvas.toDataURL("image/png"));
    }

    const frameWidth = thermalFrame.imageWidth;
    const frameHeight = thermalFrame.imageHeight;

    canvas.width = frameWidth;
    canvas.height = frameHeight;

    let ctx = canvas.getContext("2d");

    ctx.font = "14px Arial";
    ctx.strokeStyle = "#fff";
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.lineWidth = 2;

    const image = document.createElement("img");
    image.crossOrigin = "anonymous";

    image.onload = () => {
      ctx.drawImage(image, 0, 0, frameWidth, frameHeight);

      const imageMetadata = get(thermalFrame, "imageMetadata");

      if (imageMetadata) {
        const referencePoint = get(
          imageMetadata,
          "thermalMetadata.referencePoint",
          null
        );

        if (referencePoint && showAnnotations) {
          const referencePointX = referencePoint.coordinates[0];
          const referencePointY = referencePoint.coordinates[1];

          ctx.beginPath();
          ctx.arc(
            referencePointX,
            referencePointY,
            referencePoint.radius * 10,
            0,
            Math.PI * 2,
            true
          );
          decorateArc(
            ctx,
            referencePoint.coordinates[0],
            referencePoint.coordinates[1],
            "Control"
          );

          const thermalPoints = get(
            imageMetadata,
            "thermalMetadata.thermalPoints",
            null
          );

          if (thermalPoints) {
            for (const index in thermalPoints) {
              const currentX = thermalPoints[index].coordinates[0];
              const currentY = thermalPoints[index].coordinates[1];

              ctx.beginPath();
              ctx.arc(
                currentX,
                currentY,
                thermalPoints[index].radius * 10,
                0,
                Math.PI * 2,
                true
              );
              decorateArc(
                ctx,
                currentX,
                currentY,
                (
                  thermalPoints[index].deltaTemperature -
                  referencePoint.deltaTemperature
                ).toFixed(1)
              );
              ctx.stroke();
            }
          }
        }
      }

      resolve(canvas.toDataURL("image/png"));
    };

    image.src = thermalFrame.mediumUrl;
  } catch (err) {
    console.error(err);
    reject(err);
  }
}

export const getWoundImages = async frames => {
  const standardFrame = frames.find(frame =>
    STANDARD_FRAME_TYPES.includes(get(frame, "frameType", ""))
  );

  const absoluteFrame = frames.find(
    frame => get(frame, "frameType", "") === "thermal"
  );

  const deltaFrame = frames.find(
    frame => get(frame, "frameType", "") === "delta"
  );

  const fluorescentFrame = frames.find(
    frame => get(frame, "frameType", "") === "fluorescent"
  );

  const standardFrameMediumURL = get(standardFrame, "mediumUrl", "");
  const standardFrameContentType = get(standardFrame, "contentType", "");

  const absoluteFrameMediumURL = get(absoluteFrame, "mediumUrl", "");

  const deltaFrameMediumURL = get(deltaFrame, "mediumUrl", "");

  const fluorescentFrameMediumURL = get(fluorescentFrame, "mediumUrl", "");

  let standardWoundImage;
  let absoluteWoundImage;
  let deltaWoundImage;
  let fluorescentWoundImage;

  if (standardFrameMediumURL) {
    try {
      standardWoundImage = await remoteImageToDataURL(standardFrameMediumURL);
    } catch (err) {
      console.warn(err);
    }
  }

  if (absoluteFrameMediumURL) {
    try {
      absoluteWoundImage = await new Promise((resolve, reject) =>
        getAnnotatedAndTracedThermalImage(absoluteFrame, false, resolve, reject)
      );
    } catch (err) {
      console.warn(err);
    }
  }

  if (deltaFrameMediumURL) {
    try {
      deltaWoundImage = await new Promise((resolve, reject) =>
        getAnnotatedAndTracedThermalImage(deltaFrame, true, resolve, reject)
      );
    } catch (err) {
      console.warn(err);
    }
  }

  if (fluorescentFrameMediumURL) {
    try {
      fluorescentWoundImage = await remoteImageToDataURL(
        fluorescentFrameMediumURL
      );
    } catch (err) {
      console.warn(err);
    }
  }

  const result = {};

  if (standardWoundImage) {
    result["standardWoundImage"] = {
      stack: [
        {
          image: standardWoundImage,
          width: 225
        }
      ],
      alignment: "right",
      width: 225
    };
  }

  if (absoluteWoundImage) {
    result["absoluteWoundImage"] = {
      stack: [
        {
          text: i18n.t(`headers.controlView`),
          style: "h2"
        },
        {
          image: absoluteWoundImage,
          width: 210
        }
      ],
      alignment: "left",
      style: "columnBody",
      width: 300
    };
  }

  if (deltaWoundImage) {
    result["deltaWoundImage"] = {
      stack: [
        {
          text: i18n.t(`headers.differenceView`),
          style: "h2"
        },
        {
          image: deltaWoundImage,
          width: 210
        },
        {
          image:
            "data:image/png;base64,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",
          width: 210
        }
      ],
      alignment: "left",
      style: "columnBody",
      width: 300
    };
  }

  if (fluorescentWoundImage) {
    result["fluorescentWoundImage"] = {
      stack: [
        {
          text: i18n.t(`headers.fluoroEnhanced`),
          style: "h2"
        },
        {
          image: fluorescentWoundImage,
          width: 210
        }
      ],
      alignment: "left",
      style: "columnBody",
      width: 300
    };
  }

  result["noWoundImage"] = {
    stack: [
      {
        canvas: [
          {
            type: "rect",
            x: 0,
            y: 0,
            w: 225,
            h: 260
          }
        ],
        marginBottom: 6
      },
      {
        text:
          standardFrameContentType == "declined"
            ? "Patient Declined Photo"
            : "No Image",
        fontSize: 14,
        absolutePosition: { x: 420, y: 330 },
        alignment: "left"
      }
    ],
    alignment: "right",
    width: 225
  };

  return result;
};

export default getWoundImages;
